import { t } from '@trimble/i18n';
import { createMemo, createSignal, Show, type JSX } from 'solid-js';
import { DropDownMenu, Alert } from '@tcweb/connect-ui';
import { customElement } from 'solid-element';
import { AdoptReusableStylesheets } from '@trimble/stylesheets';
import { uiComponentsStylesheets } from '../../stylesheets';

interface Option<T = string> {
  key: T;
  label: string;
}

export interface DropdownSelectProps<T extends string> {
  options: Option<T>[];
  value: T | null | undefined;
  onValueChange?(value: T | null): void;
  disabled?: boolean;
  position?:
    | 'left'
    | 'right'
    | 'top left'
    | 'top right'
    | 'middle'
    | 'top middle';
  anchorClassName?: string;
  dropDownClassName?: string;
  showOnScroll?: boolean;
}

declare module 'solid-js' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      /* eslint-disable @typescript-eslint/naming-convention */
      'trb-dropdown-select': Partial<HTMLElement> &
        Partial<DropdownSelectProps<string>> & {
          'prop:options'?: DropdownSelectProps<string>['options'];
          'prop:value'?: Exclude<
            DropdownSelectProps<string>['value'],
            undefined
          >;
          'prop:onValueChange'?: Exclude<
            DropdownSelectProps<string>['onValueChange'],
            undefined
          >;
          'prop:disabled'?: Exclude<
            DropdownSelectProps<string>['disabled'],
            undefined
          >;
          'prop:position'?: Exclude<
            DropdownSelectProps<string>['position'],
            undefined
          >;
          'prop:anchorClassName'?: Exclude<
            DropdownSelectProps<string>['anchorClassName'],
            undefined
          >;
          'prop:dropDownClassName'?: Exclude<
            DropdownSelectProps<string>['dropDownClassName'],
            undefined
          >;
          'prop:showOnScroll'?: Exclude<
            DropdownSelectProps<string>['showOnScroll'],
            undefined
          >;
        };
      /* eslint-enable @typescript-eslint/naming-convention */
    }
  }
}

export const DropdownSelect = <T extends string>(
  props: DropdownSelectProps<T>
): JSX.Element => {
  const [isOpen, setIsOpen] = createSignal(false);

  const anchorIcon = createMemo(() => {
    return isOpen() ? 'tc-icon-arrow-drop-up' : 'tc-icon-arrow-drop-down';
  });

  const selectedOption = createMemo(() => {
    return props.value ?? props.options?.[0]?.key;
  });

  const selectedOptionLabel = createMemo(() => {
    if (props.options == null) {
      return undefined;
    }
    return props.options?.find((option) => option.key === selectedOption())
      ?.label;
  });

  const dropdownOptions = createMemo(() =>
    props.options?.map((option) => ({
      text: option.label,
      value: option.key,
    }))
  );

  const handleMenuOpen = (open: boolean): void => {
    setIsOpen(open);
  };

  const handleSelectionChanged = (index: number): void => {
    const selectedOption = props.options[index];
    props.onValueChange?.(
      selectedOption.key === '' ? null : selectedOption.key
    );
  };

  return (
    <>
      <Show
        when={!props.options?.length}
        fallback={
          <DropDownMenu
            type="button"
            anchorText={selectedOptionLabel?.() || '---'}
            anchorIcon={anchorIcon()}
            // NOTE: default to right, TODO: right is not working but undefined has the same effect.
            anchorIconPosition={undefined}
            anchorClassName={
              props.anchorClassName
                ? `button ${props.anchorClassName}`
                : 'button p-0 text-gray-8 text-xs font-normal min-h-[inherit]'
            }
            dropDownOptions={dropdownOptions()}
            // Reset the transform-Y to zero, this is needed when using the component inside panel-layout.
            dropDownClassName={
              props.dropDownClassName
                ? props.dropDownClassName
                : '!translate-y-0 mt-1'
            }
            optionsType="both"
            selected={props.value != null ? selectedOption() : undefined}
            position={props.position}
            disabled={props.disabled}
            showOnScroll={props.showOnScroll ?? true}
            onMenuOpen={handleMenuOpen}
            onSelectionChanged={(_selected, index) => {
              handleSelectionChanged(index);
            }}
          />
        }
      >
        <Alert type="info">
          {t('shared.ui.components.selectionCouldNotBeLoaded')}
        </Alert>
      </Show>
    </>
  );
};

export const defineDropdownSelect: () => CustomElementConstructor = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return customElement<DropdownSelectProps<any>>(
    'trb-dropdown-select',
    {
      options: [],
      value: undefined,
      onValueChange: undefined,
      disabled: undefined,
      position: undefined,
      anchorClassName: undefined,
      dropDownClassName: undefined,
      showOnScroll: undefined,
    },
    (props) => (
      <>
        <AdoptReusableStylesheets stylesheets={uiComponentsStylesheets} />

        <DropdownSelect {...props} />
      </>
    )
  );
};
